import { onMounted } from 'vue'
import useGet from '../../../core/composables/api/useGet'
import useDelete from '../../../core/composables/api/useDelete'
import { useRoute, useRouter } from 'vue-router'
import type { Member } from '../../../core/models/member/member'
import type { QuestionGroup } from '../../give-feedback/models/question-group'
import type { FeedbackTopicType } from '@/core/models/feedback-topic'
import type { Perspective } from '@/core/models/perspective'
import type { JSONContent } from '@tiptap/vue-3'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { useI18n } from 'vue-i18n'
import { useSnackStore } from '@/stores/snack.store'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '@/core/events/event-key'

export const useSurveyMemberFeedback = () => {
  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()
  const snackStore = useSnackStore()
  const feedbackUpdatedBus = useEventBus(EventKey.feedbackUpdated)

  const surveyId = route.params.surveyId as string
  const memberId = route.params.memberId as string

  const {
    data: surveyMemberFeedback,
    status: loadingStatus,
    load,
  } = useGet<SurveyMemberFeedback>(`feedback/${surveyId}/member/${memberId}`)

  const { remove, status: deleteStatus } = useDelete<void>(`survey/${surveyId}/self-feedback`)

  onMounted(async () => {
    await load()
  })

  const deleteSelfFeedback = async () => {
    await remove({})

    switch (deleteStatus.value) {
      case ApiRequestStatus.Success:
        snackStore.showInfo(t('self-feedback.delete.success'))
        feedbackUpdatedBus.emit({ feedbackId: surveyId })
        await router.push({ name: 'feedbacks' })
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('self-feedback.delete.error'))
        break
    }
  }

  return { surveyMemberFeedback, loadingStatus, deleteSelfFeedback }
}

export interface SurveyMemberFeedback {
  readonly id: string
  readonly title: string | null
  readonly createdAt: string
  readonly message?: JSON
  readonly isReceiver: boolean
  readonly isSender: boolean
  readonly isSelf: boolean
  /** Perspective key for this feedback (e.g. self, peers, parents). */
  readonly perspectiveKey: string
  readonly origin: 'coach' | 'personal'
  readonly member?: Member
  readonly feedbackTopics: ReadonlyArray<{
    readonly id: string
    readonly title: string
    readonly icon?: string
    readonly type: FeedbackTopicType
    readonly perspectives: ReadonlyArray<Perspective>
    readonly includeSelf: boolean
    readonly questionGroups: ReadonlyArray<QuestionGroup>
    readonly feedbacks: ReadonlyArray<{
      readonly id: string
      readonly questionGroupId: string
      readonly questionId: string
      readonly text: JSONContent | null
      readonly value: number | null
    }>
  }>
}
